import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';

import {
  createChapter,
  createCourse,
  enrollStudent,
  listChapters,
  listCourses,
} from '../features/instructor/instructorApi';
import type { Chapter, Course } from '../features/instructor/instructorApi';

interface CourseManagerProps {
  selectedCourseId: string | null;
  selectedChapterId: string | null;
  onSelectCourse: (courseId: string) => void;
  onSelectChapter: (chapterId: string) => void;
  refreshToken?: number;
}

interface CourseListProps {
  courses: Course[];
  selectedCourseId: string | null;
  onSelect: (courseId: string) => void;
}

function CourseList({ courses, selectedCourseId, onSelect }: CourseListProps) {
  return (
    <ul>
      {courses.map((course) => (
        <li key={course.id}>
          <button type="button" aria-pressed={course.id === selectedCourseId} onClick={() => onSelect(course.id)}>
            {course.title}
          </button>
        </li>
      ))}
    </ul>
  );
}

interface ChapterListProps {
  chapters: Chapter[];
  selectedChapterId: string | null;
  onSelect: (chapterId: string) => void;
}

function ChapterList({ chapters, selectedChapterId, onSelect }: ChapterListProps) {
  return (
    <ul>
      {chapters.map((chapter) => (
        <li key={chapter.id}>
          <button
            type="button"
            aria-pressed={chapter.id === selectedChapterId}
            onClick={() => onSelect(chapter.id)}
          >
            {chapter.title}
          </button>
        </li>
      ))}
    </ul>
  );
}

function readErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function CourseManager({
  selectedCourseId,
  selectedChapterId,
  onSelectCourse,
  onSelectChapter,
  refreshToken,
}: CourseManagerProps) {
  const [courses, setCourses] = useState<Course[]>([]);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [localRefresh, setLocalRefresh] = useState(0);

  const [courseTitle, setCourseTitle] = useState('');
  const [courseDescription, setCourseDescription] = useState('');
  const [chapterTitle, setChapterTitle] = useState('');
  const [enrollUserId, setEnrollUserId] = useState('');

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [enrollMessage, setEnrollMessage] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;

    async function loadCourses() {
      try {
        const result = await listCourses();
        if (!ignore) {
          setCourses(result);
        }
      } catch (error) {
        if (!ignore) {
          setErrorMessage(readErrorMessage(error, 'Impossible de charger les cours.'));
        }
      }
    }

    void loadCourses();
    return () => {
      ignore = true;
    };
  }, [refreshToken, localRefresh]);

  useEffect(() => {
    let ignore = false;

    async function loadChapters() {
      if (!selectedCourseId) {
        if (!ignore) {
          setChapters([]);
        }
        return;
      }
      try {
        const result = await listChapters(selectedCourseId);
        if (!ignore) {
          setChapters(result);
        }
      } catch (error) {
        if (!ignore) {
          setErrorMessage(readErrorMessage(error, 'Impossible de charger les chapitres.'));
        }
      }
    }

    void loadChapters();
    return () => {
      ignore = true;
    };
  }, [selectedCourseId, refreshToken, localRefresh]);

  async function handleCreateCourse(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setErrorMessage(null);
    try {
      const { courseId } = await createCourse(courseTitle, courseDescription);
      setCourseTitle('');
      setCourseDescription('');
      setLocalRefresh((current) => current + 1);
      onSelectCourse(courseId);
    } catch (error) {
      setErrorMessage(readErrorMessage(error, 'Impossible de créer le cours.'));
    }
  }

  async function handleCreateChapter(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedCourseId) {
      return;
    }
    setErrorMessage(null);
    try {
      const nextOrderIndex = chapters.length + 1;
      const { chapterId } = await createChapter(selectedCourseId, chapterTitle, nextOrderIndex);
      setChapterTitle('');
      setLocalRefresh((current) => current + 1);
      onSelectChapter(chapterId);
    } catch (error) {
      setErrorMessage(readErrorMessage(error, 'Impossible de créer le chapitre.'));
    }
  }

  async function handleEnroll(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedCourseId) {
      return;
    }
    setErrorMessage(null);
    setEnrollMessage(null);
    try {
      await enrollStudent(selectedCourseId, enrollUserId.trim() || undefined);
      setEnrollUserId('');
      setEnrollMessage('Étudiant inscrit avec succès.');
    } catch (error) {
      setErrorMessage(readErrorMessage(error, "Impossible d'inscrire l'étudiant."));
    }
  }

  return (
    <div style={{ display: 'grid', gap: '1.5rem' }}>
      {errorMessage && <p role="alert">{errorMessage}</p>}

      <section>
        <h2>Mes cours</h2>
        <form onSubmit={handleCreateCourse} aria-label="Créer un cours">
          <div style={{ display: 'grid', gap: '0.5rem', maxWidth: 420 }}>
            <label>
              Titre du cours
              <input
                type="text"
                value={courseTitle}
                onChange={(event) => setCourseTitle(event.target.value)}
                required
              />
            </label>
            <label>
              Description
              <textarea
                value={courseDescription}
                onChange={(event) => setCourseDescription(event.target.value)}
              />
            </label>
            <button type="submit">Créer le cours</button>
          </div>
        </form>

        <CourseList courses={courses} selectedCourseId={selectedCourseId} onSelect={onSelectCourse} />
      </section>

      {selectedCourseId && (
        <section>
          <h2>Chapitres</h2>
          <form onSubmit={handleCreateChapter} aria-label="Créer un chapitre">
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
              <label>
                Titre du chapitre
                <input
                  type="text"
                  value={chapterTitle}
                  onChange={(event) => setChapterTitle(event.target.value)}
                  required
                />
              </label>
              <button type="submit">Ajouter le chapitre</button>
            </div>
          </form>

          <ChapterList chapters={chapters} selectedChapterId={selectedChapterId} onSelect={onSelectChapter} />
        </section>
      )}

      {selectedCourseId && (
        <section>
          <h2>Inscriptions</h2>
          <form onSubmit={handleEnroll} aria-label="Inscrire un étudiant">
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
              <label>
                Identifiant de l'étudiant
                <input
                  type="text"
                  value={enrollUserId}
                  onChange={(event) => setEnrollUserId(event.target.value)}
                  placeholder="UUID de l'étudiant"
                />
              </label>
              <button type="submit">Inscrire</button>
            </div>
          </form>
          {enrollMessage && <p>{enrollMessage}</p>}
        </section>
      )}
    </div>
  );
}

export default CourseManager;
